import { lazy, Suspense, useId, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { cn } from '@hefesto/ui';
import type { NotificationCenter } from '../lib/notifications';

const loadPanel = () => import('./NotificationPanel');
const NotificationPanel = lazy(loadPanel);

/**
 * Campana del shell (01C): contador de no leídas de todas las bandejas y panel con la lista
 * (chunk perezoso, precargado al pasar el mouse o enfocar). Accesible: botón con `aria-expanded`,
 * panel no modal con foco inicial en su título, Escape y clic afuera cierran, y los avisos nuevos
 * se anuncian con cortesía.
 */

export function NotificationBell({
  center,
  placement,
}: {
  center: NotificationCenter;
  placement: 'sidebar' | 'topbar';
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const n = center.totalUnread;
  if (center.sources.length === 0) return null;
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onPointerEnter={() => void loadPanel()}
        onFocus={() => void loadPanel()}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Notificaciones${n ? `, ${n > 99 ? 'más de 99' : n} sin leer` : ''}`}
        className={cn(
          'relative grid size-10 place-items-center rounded-md transition-colors',
          open ? 'bg-forja text-hueso' : 'text-ceniza hover:bg-forja hover:text-hueso',
        )}
      >
        <Bell className="size-[18px]" aria-hidden />
        {n > 0 ? (
          <span
            aria-hidden
            className="absolute right-1 top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-brasa px-1 font-mono text-[10.5px] font-semibold leading-none text-carbon ring-2 ring-hierro"
          >
            {n > 99 ? '99+' : n}
          </span>
        ) : null}
      </button>
      <LiveAnnouncer center={center} silent={open} />
      {open ? (
        <Suspense fallback={null}>
          <NotificationPanel
            id={panelId}
            center={center}
            placement={placement}
            onClose={() => {
              setOpen(false);
              buttonRef.current?.focus();
            }}
            anchor={buttonRef}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

function LiveAnnouncer({ center, silent }: { center: NotificationCenter; silent: boolean }) {
  // El texto cambia con cada aviso (lleva la hora), así el lector de pantalla lo anuncia de nuevo.
  const at = center.lastArrival?.at;
  const text =
    at && !silent
      ? `Notificación nueva, ${new Date(at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.`
      : '';
  return (
    <span aria-live="polite" className="sr-only">
      {text}
    </span>
  );
}
